/* @refresh reload */
import './index.css'
import { Routes } from '@generouted/solid-router'
import { Meta, MetaProvider, Title } from '@solidjs/meta'
import { createRoot, createSignal, onMount } from 'solid-js'
import { ErrorBoundary, render, Show } from 'solid-js/web'
import Button from '~/components/Button'
import Icon from '~/components/Icon'
import UpdateBanner from '~/components/UpdateBanner'
import { ToastProvider } from '~/components/useToast'
import { ThemeProvider } from '~/contexts/ThemeContext'
import { AuthProvider } from './contexts/AuthContext'
import { CompanyProvider } from './contexts/CompanyContext'
import { PinnedProvider } from './contexts/PinnedContext'

const root = document.getElementById('root')
const icons = import.meta.glob('~/icons/*.svg', { eager: true, query: '?component-solid' })

if (import.meta.env.DEV && !(root instanceof HTMLElement)) {
  throw new Error('Root element not found. Did you forget to add it to your index.html? Or maybe the id attribute got misspelled?')
}

// ─── Service Worker Update Logic ────────────────────────────────────────────
// Se inicializa UNA SOLA VEZ a nivel de módulo, fuera de cualquier componente.
// createRoot() permite usar señales de SolidJS fuera de un árbol reactivo.

const [needRefresh, setNeedRefresh] = createRoot(() => createSignal(false))

function activateUpdate() {
  // Siempre obtenemos la registration en el momento del click (no guardamos referencia stale)
  navigator.serviceWorker.getRegistration().then(reg => {
    const sw = reg?.waiting
    if (sw) {
      sw.postMessage({ type: 'SKIP_WAITING' })
    }
  })
}

if ('serviceWorker' in navigator) {
  // Función central: dada una registration, conecta todos los listeners
  function watchRegistration(reg: ServiceWorkerRegistration) {
    // ¿Ya hay un SW esperando? (ej. tab recargada o venía de antes)
    if (reg.waiting) {
      setNeedRefresh(true)
      return
    }

    // Nuevo SW descargándose mientras la app está abierta
    reg.addEventListener('updatefound', () => {
      const newSW = reg.installing
      if (!newSW) return

      newSW.addEventListener('statechange', () => {
        if (newSW.state === 'installed' && navigator.serviceWorker.controller) {
          setNeedRefresh(true)
        }
      })
    })
  }

  // 1. Al cargar: esperar a que el SW esté listo
  navigator.serviceWorker.ready.then(reg => {
    watchRegistration(reg)

    // 2. Polling cada 60s: fuerza que el navegador compruebe si hay un SW nuevo
    //    (el navegador solo lo comprueba automáticamente cada 24h o en navegación)
    setInterval(() => reg.update(), 60_000)
  })

  // 3. Al volver a la pestaña (visibilitychange): forzar comprobación de actualización
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      navigator.serviceWorker.getRegistration().then(reg => {
        if (!reg) return
        if (reg.waiting) {
          setNeedRefresh(true)
        } else {
          reg.update()
        }
      })
    }
  })

  // 4. Cuando el SW activa el SKIP_WAITING → controllerchange → recarga
  let refreshing = false
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (!refreshing) {
      refreshing = true
      window.location.reload()
    }
  })
}
// ─────────────────────────────────────────────────────────────────────────────

const App = () => {
  /**
   * index.html trae un <title> estático (para quien no ejecuta JS) y solid-meta
   * añade el suyo. Con dos títulos en el head el navegador se queda con el
   * primero, así que la pestaña nunca mostraba la página actual: se retira el
   * estático en cuanto la app arranca.
   */
  onMount(() => {
    const staticTitle = document.head.querySelector('title')
    if (staticTitle && document.head.querySelectorAll('title').length > 1) staticTitle.remove()
  })

  return (
    <MetaProvider>
      <ErrorBoundary
        fallback={(err, reset) => (
          <div class="h-dvh flex flex-col items-center justify-center text-center p-6 bg-surface">
            <div class="p-4 rounded-2xl bg-danger-soft mb-6">
              <Icon icon="material-symbols-light:error-outline" size={56} class="text-danger" />
            </div>
            <h2 class="text-xl font-semibold text-foreground mb-2">Algo salió mal</h2>
            <p class="text-foreground-subtle max-w-md mb-6">{String(err)}</p>
            <div class="flex gap-3">
              <Button onClick={reset} prependIcon="material-symbols-light:refresh">
                Reintentar
              </Button>
              <Button variant="outlined" onClick={() => (window.location.href = '/')} prependIcon="material-symbols-light:home">
                Ir al inicio
              </Button>
            </div>
          </div>
        )}
      >
        <ThemeProvider icons={icons}>
          <PinnedProvider>
            <AuthProvider>
              <CompanyProvider>
                {/* Meta Tags Globales */}
                <Meta name="description" content="Florez Jaramillo Solutions - Expertos en desarrollo de software, aplicaciones web y móviles. Soluciones digitales personalizadas para empresas." />
                <Meta
                  name="keywords"
                  content="soluciones tecnológicas, desarrollo de software, para aplicaciones web, en aplicaciones móviles, servicios digitales, seguridad IT, Florez Jaramillo, desarrollo personalizado, transformación digital"
                />
                <Meta property="og:title" content="Florez Jaramillo - Soluciones Tecnológicas Innovadoras" />
                <Meta property="og:description" content="Expertos en desarrollo de software y soluciones digitales personalizadas. Transformamos el futuro digital de tu empresa." />
                <Meta property="og:type" content="website" />
                <Meta property="og:locale" content="es_CO" />
                <Meta property="og:image" content="/src/assets/og-image.jpg" />
                <Meta property="og:url" content="https://florezjaramillo.com" />

                {/* Additional SEO Meta Tags */}
                <Meta name="geo.region" content="CO" />
                <Meta name="geo.placename" content="Colombia" />
                <Meta name="language" content="es" />
                <Meta name="coverage" content="Worldwide" />
                <Meta name="distribution" content="Global" />
                <Meta name="rating" content="General" />

                <Title>Florez Jaramillo Solutions - Soluciones Tecnológicas</Title>
                <ToastProvider>
                  <Routes />
                </ToastProvider>
              </CompanyProvider>
            </AuthProvider>
          </PinnedProvider>
        </ThemeProvider>
      </ErrorBoundary>
      <Show when={needRefresh()}>
        <UpdateBanner onUpdate={activateUpdate} />
      </Show>
    </MetaProvider>
  )
}

render(() => <App />, root!)
